// =============================================================================
// BP-07-03: DiagnosisHero Component
// =============================================================================

import { Link } from 'react-router-dom';
import { ChevronRight, BookOpen, FileText, Users } from 'lucide-react';
import type { Diagnosis } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface DiagnosisHeroProps {
  diagnosis: Diagnosis;
  className?: string;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function DiagnosisHero({ diagnosis, className }: DiagnosisHeroProps) {
  return (
    <section
      className={cn(
        'relative bg-navy text-white overflow-hidden',
        className
      )}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Gradient overlay */}
        {diagnosis.heroImageUrl && (
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: `url(${diagnosis.heroImageUrl})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/80" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-white/60 mb-6">
          <Link to="/" className="hover:text-white transition-colors">
            Home
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/diagnoses" className="hover:text-white transition-colors">
            Diagnoses
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">{diagnosis.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Text */}
          <div>
            {/* Icon */}
            {diagnosis.iconUrl && (
              <div className="w-16 h-16 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-6">
                <img
                  src={diagnosis.iconUrl}
                  alt=""
                  className="w-10 h-10"
                />
              </div>
            )}

            {/* Title */}
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium mb-4">
              {diagnosis.name}
            </h1>

            {/* Short name / Alias */}
            {diagnosis.shortName && diagnosis.shortName !== diagnosis.name && (
              <p className="text-xl text-terracotta-light mb-4">
                Also known as: {diagnosis.shortName}
              </p>
            )}

            {/* Description */}
            {diagnosis.description && (
              <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-xl">
                {diagnosis.description}
              </p>
            )}

            {/* ICD-10 Codes */}
            {diagnosis.icd10Codes.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {diagnosis.icd10Codes.map((code) => (
                  <span
                    key={code}
                    className="px-3 py-1 text-sm bg-white/10 rounded-full"
                  >
                    ICD-10: {code}
                  </span>
                ))}
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <a
                href="#resources"
                className="inline-flex items-center gap-2 px-6 py-3 bg-sage text-white font-medium rounded-lg hover:bg-sage-dark transition-colors"
              >
                <BookOpen className="w-5 h-5" />
                Browse Resources
              </a>
              <a
                href="#specialists"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/20 transition-colors"
              >
                <Users className="w-5 h-5" />
                Find Specialists
              </a>
            </div>
          </div>

          {/* Right Column - Stats */}
          <div className="grid sm:grid-cols-3 gap-4 lg:gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-sage/20 flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-sage-light" />
              </div>
              <div className="text-3xl font-display font-semibold text-white mb-1">
                {diagnosis.resourceCount}
              </div>
              <div className="text-sm text-white/60">Resources</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-terracotta/20 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-terracotta-light" />
              </div>
              <div className="text-3xl font-display font-semibold text-white mb-1">
                {diagnosis.specialistTypes.length}
              </div>
              <div className="text-sm text-white/60">Specialist Types</div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mx-auto mb-3">
                <BookOpen className="w-6 h-6 text-white/80" />
              </div>
              <div className="text-3xl font-display font-semibold text-white mb-1">
                {diagnosis.relatedDiagnoses.length}
              </div>
              <div className="text-sm text-white/60">Related Conditions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-auto"
          preserveAspectRatio="none"
        >
          <path
            d="M0 100V50C240 16.6667 480 0 720 0C960 0 1200 16.6667 1440 50V100H0Z"
            className="fill-cream"
          />
        </svg>
      </div>
    </section>
  );
}

export default DiagnosisHero;

